import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Tab,
    AppBar,
    useMediaQuery,
    useTheme,
    Box,
    Badge,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import {
    Close as CloseIcon,
    Menu as MenuIcon,
    ChevronRight as ChevronRightIcon,
    AccountCircle,
    PlayCircle,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useUnsavedChanges } from './UnsavedChangesContext';
import StoreContext from '../../react-core-lib/components/app/store-context';
import searchIcon from '../../assets/icons/Search.svg';
import handoutIcon from '../../assets/icons/Handout.svg';
import playCircleIcon from '../../assets/icons/PlayCircle.svg';

import CTAUpgradeDialog from '../UIControls/CTAUpgradeDialog';
import { getCTAUpgradeImageUrl, cetraBlueLogo, cetraMobileLogo } from '../../config';

const Navigation = observer(() => {
    const theme = useTheme();
    const isNarrowLandscape = useMediaQuery('(max-width: 920px)');
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showBurgerMenu = isMobileOrTablet || isNarrowLandscape;
    const { promptUnsavedChanges } = useUnsavedChanges();
    const location = useLocation();
    const navigate = useNavigate();
    const { userDataStore } = useContext(StoreContext);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAOpenProfessional, setUpgradeCTAOpenProfessional] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser' ? true : false;

    const handleNavigate = (path) => {
        promptUnsavedChanges(path);
        setMobileOpen(false);
    };

    const handleHandoutsClick = () => {
        if (
            userDataStore?.subscriptionData?.accountType === 'Student' ||
            userDataStore?.subscriptionData?.accountType === 'Professional'
        ) {
            setUpgradeCTAOpen(true);
        } else {
            handleNavigate('/handouts');
        }
    };

    const handleMyLibraryClick = () => {
        handleNavigate('/my-library');
    };

    const handleMyClinicClick = () => {
        handleNavigate('/my-clinic');
    };

    // blocked for now
    // const handle3DModelsClick = () => {
    //     if (userDataStore?.subscriptionData?.accountType === 'Student') {
    //         setUpgradeCTAOpenProfessional(true);
    //     } else {
    //         handleNavigate('/3d-models');
    //     }
    // };

    const isHandoutsOrSearchRoute =
        location.pathname.includes('/handout') ||
        location.pathname.includes('/search') ||
        location.pathname.includes('/handouts') ||
        location.pathname.includes('/feedback');

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const renderDesktopNavigation = () => (
        <>
            <Box sx={{ width: 120 }} />
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flex={1}
                sx={{
                    gap: '4px',
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    '@media (max-width: 900px)': {
                        '& .nav-text': {
                            fontSize: '0.8rem',
                            padding: '12px 8px',
                        },
                    },
                    '@media (max-width: 768px)': {
                        '& .MuiTab-root.MuiButtonBase-root.nav-text': {
                            fontSize: '0.75rem!important',
                            padding: '0px',
                        },
                    },
                }}
            >
                <Tab
                    disableRipple
                    label="My Library"
                    onClick={handleMyLibraryClick}
                    className="nav-text"
                    sx={{
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                />

                {userDataStore?.subscriptionData?.accountType !== 'Student' && (
                    <Tab
                        disableRipple
                        label="My Clinic"
                        onClick={handleMyClinicClick}
                        className="nav-text"
                        sx={{
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                        }}
                    />
                )}

                <Tab
                    disableRipple
                    label="Archive"
                    onClick={() => handleNavigate('/archive')}
                    className="nav-text"
                    sx={{
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                />
                {/* blocked for now */}
                {/* <Tab
                    disableRipple
                    label="3D Models"
                    onClick={handle3DModelsClick}
                    className="nav-text"
                    sx={{
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                /> */}
            </Box>

            {/* Right side - Search, Handouts, Profile */}
            <Box display="flex" alignItems="center">
                {/* Search Icon */}
                <Box>
                    <Tab
                        disableRipple
                        icon={<img src={searchIcon} alt="Search Icon" />}
                        onClick={() => handleNavigate('/search')}
                        sx={{ minWidth: 'auto', padding: '12px 0px' }}
                    />
                </Box>

                {/* Handout Icon */}
                <Box sx={{ marginLeft: '12px' }}>
                    <Badge
                        badgeContent={
                            userDataStore?.userData?.handouts
                                ? JSON.parse(userDataStore.userData.handouts).length
                                : null
                        }
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: '#FC96A2',
                                color: 'white',
                                transform: 'translateZ(1px)',
                            },
                        }}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Tab
                            disableRipple
                            icon={<img src={handoutIcon} alt="Handouts Icon" />}
                            onClick={handleHandoutsClick}
                            sx={{ minWidth: 'auto', padding: '12px 0px' }}
                        />
                    </Badge>
                </Box>

                {/* Onboarding Video Button */}
                <Box sx={{ marginLeft: '12px' }}>
                    <Tab
                        disableRipple
                        icon={<img src={playCircleIcon} alt="Play Icon" />}
                        onClick={() => {
                            localStorage.setItem('onboardingComplete', 'true');
                            window.dispatchEvent(new Event('trigger-onboarding-modal'));
                        }}
                        sx={{ minWidth: 'auto', padding: '12px 0px' }}
                    />
                </Box>

                {/* Settings */}
                <Box sx={{ marginLeft: '12px' }}>
                    <Tab
                        disableRipple
                        icon={
                            userDataStore?.subscriptionData?.accountType.includes('Clinic') &&
                            userDataStore?.userData?.clinicImageId ? (
                                <img
                                    width={40}
                                    style={{ marginBottom: 0, marginRight: 8 }}
                                    src={userDataStore?.userData?.clinicImageId}
                                    alt="Clinic User Icon"
                                />
                            ) : (
                                <AccountCircle style={{ marginBottom: 0, marginRight: 8 }} fontSize="large" />
                            )
                        }
                        label={
                            userDataStore?.subscriptionData?.accountType.includes('Clinic') &&
                            userDataStore?.userData?.clinicName
                                ? userDataStore?.userData?.clinicName
                                : 'Profile'
                        }
                        onClick={() =>
                            isMultiUser ? handleNavigate('/settings-multiuser') : handleNavigate('/settings')
                        }
                        sx={{
                            height: 40,
                            background: '#F6F6F6',
                            borderRadius: '36px',
                            minHeight: 'auto',
                            minWidth: null,
                            flexDirection: 'row',
                        }}
                        className="header-button"
                    />
                </Box>
            </Box>
        </>
    );

    return (
        <>
            <AppBar
                position="relative"
                color="default"
                sx={{
                    top: showBurgerMenu ? 'auto' : 0,
                    boxShadow: 'none',
                    background: 'white',
                    padding: showBurgerMenu ? '0 16px' : '0px 40px',
                    position: 'relative',
                    zIndex: 1000,
                    height: showBurgerMenu ? 'auto' : '72px',
                    marginBottom: '0',
                    bottom: showBurgerMenu ? 0 : 'auto',
                    '@media print': {
                        display: 'none',
                    },
                }}
            >
                <Box display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ opacity: 1 }}>
                    {/* Left side - Logo (and Burger Menu on mobile) */}
                    <Box display="flex" alignItems="center">
                        {showBurgerMenu ? (
                            <>
                                <Tab
                                    disableRipple
                                    sx={{ p: 0, minWidth: 'auto', marginRight: 2.5 }}
                                    icon={<img src={cetraMobileLogo} alt="Home" style={{ p: 0 }} width={40} />}
                                    onClick={() => handleNavigate('/')}
                                    className={`app-logo ${isHandoutsOrSearchRoute ? 'app-logo-search' : ''}`}
                                />
                                {/* Burger Menu Button */}
                                <IconButton
                                    color="inherit"
                                    aria-label="open drawer"
                                    edge="start"
                                    onClick={handleDrawerToggle}
                                    sx={{ backgroundColor: 'rgb(246, 246, 246)' }}
                                >
                                    <MenuIcon />
                                </IconButton>

                                {/* Mobile Menu Drawer */}
                                {mobileOpen && (
                                    <>
                                        {/* Overlay */}
                                        <Box
                                            sx={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                zIndex: 1998,
                                            }}
                                            onClick={handleDrawerToggle}
                                        />

                                        {/* Drawer */}
                                        <Box
                                            sx={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: '70%',
                                                maxWidth: '300px',
                                                height: '100vh',
                                                bgcolor: 'background.paper',
                                                zIndex: 1999,
                                                boxShadow: 3,
                                            }}
                                        >
                                            {/* Close button */}
                                            <IconButton
                                                onClick={handleDrawerToggle}
                                                sx={{
                                                    position: 'absolute',
                                                    top: '16px',
                                                    left: '10px',
                                                    zIndex: 1000,
                                                    background: 'rgb(246, 246, 246)',
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>

                                            <List sx={{ marginTop: '80px' }}>
                                                <ListItem
                                                    button
                                                    onClick={handleMyLibraryClick}
                                                    sx={{ borderBottom: '1px solid #e0e0e0' }}
                                                    secondaryAction={
                                                        <ChevronRightIcon color="action" sx={{ fontSize: 16 }} />
                                                    }
                                                >
                                                    <ListItemText primary="My Library" />
                                                </ListItem>

                                                {userDataStore?.subscriptionData?.accountType !== 'Student' && (
                                                    <ListItem
                                                        button
                                                        onClick={handleMyClinicClick}
                                                        sx={{ borderBottom: '1px solid #e0e0e0' }}
                                                        secondaryAction={
                                                            <ChevronRightIcon color="action" sx={{ fontSize: 16 }} />
                                                        }
                                                    >
                                                        <ListItemText primary="My Clinic" />
                                                    </ListItem>
                                                )}

                                                <ListItem
                                                    button
                                                    onClick={() => handleNavigate('/archive')}
                                                    sx={{ borderBottom: '1px solid #e0e0e0' }}
                                                    secondaryAction={
                                                        <ChevronRightIcon color="action" sx={{ fontSize: 16 }} />
                                                    }
                                                >
                                                    <ListItemText primary="Archive" />
                                                </ListItem>

                                                {/* <ListItem
                                                    button
                                                    onClick={handle3DModelsClick}
                                                    sx={{ borderBottom: '1px solid #e0e0e0' }}
                                                    secondaryAction={
                                                        <ChevronRightIcon color="action" sx={{ fontSize: 16 }} />
                                                    }
                                                >
                                                    <ListItemText primary="3D Models" />
                                                </ListItem> */}
                                            </List>
                                        </Box>
                                    </>
                                )}
                            </>
                        ) : (
                            /* On desktop: Just Logo */
                            <Tab
                                disableRipple
                                sx={{ p: 0 }}
                                icon={<img src={cetraBlueLogo} alt="Home" style={{ p: 0 }} width={120} />}
                                onClick={() => handleNavigate('/')}
                                className={`app-logo ${isHandoutsOrSearchRoute ? 'app-logo-search' : ''}`}
                            />
                        )}
                    </Box>

                    {/* Right side - Navigation icons */}
                    {showBurgerMenu ? (
                        /* On mobile: Search, Handouts, Profile */
                        <Box display="flex" alignItems="center">
                            <Box>
                                <Tab
                                    disableRipple
                                    icon={<img src={searchIcon} alt="Search Icon" />}
                                    onClick={() => handleNavigate('/search')}
                                    sx={{ minWidth: 'auto', padding: '12px 0px' }}
                                />
                            </Box>

                            <Box sx={{ marginLeft: '12px' }}>
                                <Badge
                                    badgeContent={
                                        userDataStore?.userData?.handouts
                                            ? JSON.parse(userDataStore.userData.handouts).length
                                            : null
                                    }
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: '#FC96A2',
                                            color: 'white',
                                            transform: 'translateZ(1px)',
                                        },
                                    }}
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <Tab
                                        disableRipple
                                        icon={<img src={handoutIcon} alt="Handouts Icon" />}
                                        onClick={handleHandoutsClick}
                                        sx={{ minWidth: 'auto', padding: '12px 0px' }}
                                    />
                                </Badge>
                            </Box>

                            <Box sx={{ marginLeft: '12px' }}>
                                <Tab
                                    disableRipple
                                    icon={<img src={playCircleIcon} alt="Play Icon" />}
                                    onClick={() => {
                                        localStorage.setItem('onboardingComplete', 'true');
                                        window.dispatchEvent(new Event('trigger-onboarding-modal'));
                                    }}
                                    sx={{ minWidth: 'auto', padding: '12px 0px' }}
                                />
                            </Box>

                            <Box sx={{ marginLeft: '12px' }}>
                                <Tab
                                    disableRipple
                                    icon={
                                        userDataStore?.subscriptionData?.accountType.includes('Clinic') &&
                                        userDataStore?.userData?.clinicImageId ? (
                                            <img
                                                width={40}
                                                style={{ marginBottom: 0 }}
                                                src={userDataStore?.userData?.clinicImageId}
                                                alt="Clinic User Icon"
                                            />
                                        ) : (
                                            <AccountCircle style={{ marginBottom: 0 }} fontSize="large" />
                                        )
                                    }
                                    onClick={() =>
                                        isMultiUser
                                            ? handleNavigate('/settings-multiuser')
                                            : handleNavigate('/settings')
                                    }
                                    sx={{
                                        height: 40,
                                        background: '#F6F6F6',
                                        borderRadius: '36px',
                                        minHeight: 'auto',
                                        minWidth: 'auto',
                                        padding: 0,
                                    }}
                                    className="header-button"
                                />
                            </Box>
                        </Box>
                    ) : (
                        /* On desktop: Full navigation */
                        renderDesktopNavigation()
                    )}
                </Box>
            </AppBar>

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            <CTAUpgradeDialog
                open={upgradeCTAOpenProfessional}
                handleClose={() => {
                    setUpgradeCTAOpenProfessional(false);
                }}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details ',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />
        </>
    );
});

export default Navigation;
